import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { demandPartnersQueryKey, useDemandPartnersClient } from '../demand/partners/hooks';
import { integrationsQueryKey, useIntegrationsClient } from '../demand/integrations/hooks';
import { placementsQueryKey, usePlacementsClient } from '../supply/placements/hooks';
import { publishersQueryKey, usePublisherClient } from '../supply/publishers/hooks';
import { isIdLikeSearchTerm, SearchEntityKind, type SearchResult } from './model';
import {
  demandPartnerSearchResult,
  integrationSearchResult,
  placementSearchResult,
  publisherSearchResult,
} from './searchResultRoutes';

const LOOKUP_STALE_TIME = 60_000;

export function useIdLookup(term: string, allowedKinds: ReadonlySet<SearchEntityKind>) {
  const idLike = isIdLikeSearchTerm(term);
  const publisherClient = usePublisherClient();
  const placementsClient = usePlacementsClient();
  const demandPartnersClient = useDemandPartnersClient();
  const integrationsClient = useIntegrationsClient();

  const publisherQuery = useQuery({
    queryKey: [...publishersQueryKey, 'lookup', term],
    queryFn: () => publisherClient.get(term).catch(() => null),
    enabled: idLike && allowedKinds.has(SearchEntityKind.Publisher),
    retry: false,
    staleTime: LOOKUP_STALE_TIME,
  });
  const placementQuery = useQuery({
    queryKey: [...placementsQueryKey, 'lookup', term],
    queryFn: () => placementsClient.get(term).catch(() => null),
    enabled: idLike && allowedKinds.has(SearchEntityKind.Placement),
    retry: false,
    staleTime: LOOKUP_STALE_TIME,
  });
  const demandPartnerQuery = useQuery({
    queryKey: [...demandPartnersQueryKey, 'lookup', term],
    queryFn: () => demandPartnersClient.get(term).catch(() => null),
    enabled: idLike && allowedKinds.has(SearchEntityKind.DemandPartner),
    retry: false,
    staleTime: LOOKUP_STALE_TIME,
  });
  const integrationQuery = useQuery({
    queryKey: [...integrationsQueryKey, 'lookup', term],
    queryFn: async () => {
      const page = await integrationsClient.listByIds([term]).catch(() => null);
      return page?.content.find((row) => row.id.toLowerCase() === term.toLowerCase()) ?? null;
    },
    enabled: idLike && allowedKinds.has(SearchEntityKind.Integration),
    retry: false,
    staleTime: LOOKUP_STALE_TIME,
  });

  const results = useMemo<SearchResult[]>(() => {
    const hits: SearchResult[] = [];
    if (publisherQuery.data) hits.push(publisherSearchResult(publisherQuery.data));
    if (placementQuery.data) hits.push(placementSearchResult(placementQuery.data));
    if (demandPartnerQuery.data) hits.push(demandPartnerSearchResult(demandPartnerQuery.data));
    if (integrationQuery.data) hits.push(integrationSearchResult(integrationQuery.data));
    return idLike ? hits.filter((hit) => allowedKinds.has(hit.kind)) : [];
  }, [
    publisherQuery.data,
    placementQuery.data,
    demandPartnerQuery.data,
    integrationQuery.data,
    idLike,
    allowedKinds,
  ]);

  return {
    results,
    isLoading:
      publisherQuery.isLoading ||
      placementQuery.isLoading ||
      demandPartnerQuery.isLoading ||
      integrationQuery.isLoading,
  };
}
